import React, { useState, useEffect, useContext } from 'react';
import {
  stringToProtoDate,
  stringToProtoTradeTimeStamp,
} from '../../../services/ConvertService';
import {
  Transaction,
  ReadTransactionRequest,
  TransactionFee,
  GetFeeRequest,
  ListFeesRequest,
  ValidateTransactionsRequest,
} from '../../../proto/trnspb/transaction_grpc_web_pb';
import SelectCurrency from 'components/AutoComplete/Currency';
import DateInputWarning from 'components/Input/DateInputWarning';

import {
  Typography,
  Box,
  Select,
  MenuItem,
  TextField,
  InputLabel,
  Button,
  AccordionDetails,
  CircularProgress,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { useConfirm } from 'material-ui-confirm';
import { some } from 'lodash';
import {
  notifySuccess,
  notifyError,
} from 'components/Notification/Notification';
import { AssignmentReturn as ImportIcon } from '@material-ui/icons';
/*styles */
import useStyles from '../styles';

/*Moment JS*/
import moment from 'moment-timezone';

/*data*/
import { trnsOptions, sideOptions, entrySubTypeOptions } from '../MockData';

/*Custom components*/
import SelectAccount from '../../../components/AutoComplete/SelectAccount';
import SelectSymbol from '../../../components/AutoComplete/SelectSymbol';
import SelectEntrySubType from '../../../components/AutoComplete/SelectEntrySubType';
import TransactionUploadModal from './TransactionUploadModal';
import VendorSelect from '../../../components/AutoComplete/Vendor';
import { ServiceContext } from 'context/ServiceContext';
import {
  isBodRiskWindow,
  BOD_CANCEL_WARNING,
  cancelConfirmDescription,
} from 'lib/bodWindow';
import { useBodRiskWindow } from 'lib/useBodRiskWindow';
import { ReadPendingTrnsRequest } from '../../../proto/trnspb/pendingtrns_grpc_web_pb';

import FeesComponent from './Fees';

import { meta } from 'services/TransactionService';
import {
  stringToProtoTransactionMetadata,
  protoTransactionMetadataLocalFees,
  protoTransactionMetadataToString,
} from 'services/TransactionMetadata';
import { useProfile } from 'context/ProfileContext';
import { MetadataModal } from './MetadataModal';
import {
  affectsShortLeg,
  validateEntryTypeMetadata,
} from './validation';

// UI displays swap rate as: from 1 foreign currency to USD (e.g., 1.17 for EUR)
// Backend expects: from 1 USD to foreign currency (e.g., 0.854 for EUR)
const RECIPROCAL_ENTRY_TYPES = [
  'FXCNV',
  'ACATC',
  'CSD',
  'CSW',
  'INT',
  'JNLC',
  'PTC',
  'PTR',
  'FEE',
  'INVPNL',
  'FXPNL',
  'DTCC',
  'ACCTCLR',
  'PRDCLS',
];
// Entry types for which Ledgie backend will not fill contra symbol or swap rate automatically
export const ENTRY_TYPES_AUTOFILL_BLACKLIST = [
  'TRD',
  'OPTRD',
  'OPASN',
  'OPEXC',
  'OPCSH',
  'OCT',
  'EXTRD',
  'FXTRD',
  'FXPNL',
  'FXCNV',
];

function convertSwapRateForBackend(uiSwapRate, entryType) {
  if (!RECIPROCAL_ENTRY_TYPES.includes(entryType)) {
    return uiSwapRate;
  }
  
  if (!uiSwapRate || uiSwapRate === '' || parseFloat(uiSwapRate) === 0) {
    return uiSwapRate;
  }
  return (1 / parseFloat(uiSwapRate)).toString();
}

function formatProtoDateForInput(date) {
  if (!date) {
    return '';
  }

  return [
    date.year,
    date.month.toString().padStart(2, '0'),
    date.day.toString().padStart(2, '0'),
  ].join('-');
}

function formatTimestampForInput(timestamp) {
  if (!timestamp) {
    return '';
  }

  return moment(new Date(timestamp.seconds * 1000))
    .toISOString()
    .substring(0, 16);
}

function saveTransaction(
  transactionClient,
  params,
  trnsId,
  screen,
  cancelAndCorrect,
  pending,
  actionType,
  setMessage,
) {
  // Use the helper function to build the transaction request
  let req = buildTransactionRequest(params, screen);

  // If buildTransactionRequest returns null, validation failed
  if (!req) {
    return;
  }

  if (pending && actionType !== 'process-pending') {
    return new Promise((resolve, reject) => {
      transactionClient.createPendingTransaction(req, meta, (err, res) => {
        if (err) {
          notifyError(err.message);
          reject(err);
          return;
        }
        if (res.toObject().status === 'pending') {
          notifySuccess('Pending Transaction has been added.');
        } else {
          notifyError(
            res
              .toObject()
              .status.charAt(0)
              .toUpperCase() +
              res.toObject().status.slice(1) +
              '. ' +
              res.toObject().errorMessage
          );
        }
        resolve();
      });
    });
  } else {
    if (actionType === 'process-pending') {
      req.setTrnsId(trnsId);
      return new Promise((resolve, reject) => {
        transactionClient.processPendingTransaction(req, meta, (err, res) => {
          if (err) {
            notifyError(err.message);
            reject(err);
            return;
          }
          if (res.toObject().errorMessage === '') {
            setMessage({
              severity: 'success',
              message: 'Pending transaction has been processed.',
            });
            notifySuccess('Pending transaction has been processed.');
          } else {
            notifyError(
              res
                .toObject()
                .status.charAt(0)
                .toUpperCase() +
                res.toObject().status.slice(1) +
                '. ' +
                res.toObject().errorMessage
            );
          }
          resolve();
        });
      });
    } else if (cancelAndCorrect) {
      req.setTrnsId(trnsId);
      return new Promise((resolve, reject) => {
        transactionClient.correctTransaction(req, meta, (err, res) => {
          if (err) {
            notifyError(err.message);
            reject(err);
            return;
          }
          if (['deleted', 'rejected'].includes(res.toObject().status)) {
            // "<status>. <error-message>"
            // e.g., "Rejected. Invalid Symbol"
            notifyError(
              res
                .toObject()
                .status.charAt(0)
                .toUpperCase() +
                res.toObject().status.slice(1) +
                '. ' +
                res.toObject().errorMessage
            );
          } else {
            notifySuccess('Transaction has been corrected.');
          }
          resolve();
        });
      });
    } else {
      return new Promise((resolve, reject) => {
        transactionClient.createTransaction(req, meta, (err, res) => {
          if (err) {
            notifyError(err.message);
            reject(err);
            return;
          }
          if (res.toObject().status === 'executed') {
            notifySuccess('Transaction has been added.');
          } else if (res.toObject().status === 'split') {
            notifySuccess(res.toObject().status);
          } else {
            notifyError(
              res
                .toObject()
                .status.charAt(0)
                .toUpperCase() +
                res.toObject().status.slice(1) +
                '. ' +
                res.toObject().errorMessage
            );
          }
          resolve();
        });
      });
    }
  }
}

// Helper function to build a Transaction request object
// Returns the Transaction object or null if validation fails
function buildTransactionRequest(params, screen) {
  let req = new Transaction();

  if (screen === 'Trade') {
    if (!params.entryType) {
      notifyError('Entry Type is required.');
      return null;
    }
    if (!params.accountNumber) {
      notifyError('Account No. is required.');
      return null;
    }
    if (!params.contraAccount) {
      notifyError('Contra Account is required.');
      return null;
    }
    if (!params.side) {
      notifyError('Side is required.');
      return null;
    }
    if (!params.symbol && !hasIsinMic(params)) {
      notifyError('Symbol is required when ISIN and MIC are not provided.');
      return null;
    }
    if (!params.qty) {
      notifyError('Quantity is required.');
      return null;
    }
    if (!params.price) {
      notifyError('Price is required.');
      return null;
    }
    if (!params.executedAt) {
      notifyError('Trade Executed At is required.');
      return null;
    }
    if (!params.tradeDate) {
      notifyError('Trade Date is required.');
      return null;
    }

    if (params.isin && !params.exchangeMic) {
      notifyError('Exchange MIC is required when ISIN is provided.');
      return null;
    }

    if (params.isin && !/^[A-Z]{2}[0-9A-Z]{9}[0-9]$/.test(params.isin)) {
      notifyError(
        'ISIN must be in format: 2 letters + 9 alphanumeric + 1 digit (e.g., US0378331005).'
      );
      return null;
    }

    req.setEntryType(params.entryType);
    req.setEntrySubType(params.entrySubType);
    req.setCorrespondent(params.correspondent);
    req.setAccountNo(params.accountNumber);
    req.setContraCorrespondent(params.contraCorrespondent);
    req.setContraAccountNo(params.contraAccount);
    req.setSide(params.side);
    
    req.setSymbol(params.symbol);
    req.setCusip(params.cusip);
    req.setIsin(params.isin);
    req.setExchangeMic(params.exchangeMic);
    req.setQty(params.qty);

    req.setGrossAmt(params.grossAmount);
    req.setNetAmt(params.netAmount);
    req.setExecutedAt(stringToProtoTradeTimeStamp(params.executedAt));
    req.setTradeDate(stringToProtoDate(params.tradeDate));
    req.setSettleDate(stringToProtoDate(params.settleDate));
    req.setDescription(params.description);
    req.setExecutionId(params.executionId);
    req.setExternalId(params.externalId);
    req.setExecutingVenue(params.executingVenue);
    req.setOrderId(params.orderId);
    req.setLeavesQty(params.leavesQty);
    req.setFees(params.fees);
    req.setContraAccountDesignator(params.contraAccountDesignator);
    req.setRefId(params.refId);
    req.setPrice(params.price);

    let metadata = null;
    if (params.metadata) {
      metadata = validateMetadata(params.metadata);
      if (!metadata) return null;
      if (metadata.getLocalFees()) {
        notifyError(
          'Please specify Local Fees in the field instead of free format'
        );
        return null;
      }
      req.setMetadata(metadata);
    }
    if (
      !validateEntryTypeMetadata(
        params.entryType,
        params.entrySubType,
        params.metadata
      )
    ) {
      return null;
    }

    if (params.contraSymbol && params.contraSymbol !== 'USD') {
      req.setContraSymbol(
        params.contraSymbol.startsWith('$')
          ? params.contraSymbol
          : '$' + params.contraSymbol
      );

      if (ENTRY_TYPES_AUTOFILL_BLACKLIST.includes(params.entryType) && !params.swapRate) {
        notifyError('Swap Rate is required.');
        return null;
      }

      if (
        params.swapFeeBpsAlpa.includes('.') ||
        params.swapFeeBpsAlpa.includes('-')
      ) {
        notifyError('Swap Fee BPS should be positive integer.');
        return null;
      }
      if (
        params.swapFeeBpsCorr.includes('.') ||
        params.swapFeeBpsCorr.includes('-')
      ) {
        notifyError('Swap Fee BPS should be positive integer.');
        return null;
      }

      if (!metadata && params.localFees) {
        metadata = validateMetadata(
          '{"local_fees":"' + params.localFees + '"}'
        );
        if (!metadata) return null;
      } else if (params.localFees) {
        metadata.setLocalFees(params.localFees);
      }

      req.setSwapRate(params.swapRate);
      req.setSwapRateAt(stringToProtoTradeTimeStamp(params.swapRateAt));
      req.setLocalGrossAmt(params.localGrossAmt);
      req.setLocalNetAmt(params.localNetAmt);
      req.setSwapFeeBpsAlpa(params.swapFeeBpsAlpa);
      req.setSwapFeeBpsCorr(params.swapFeeBpsCorr);
      req.setMetadata(metadata);
    }

    let trnsFees = [];
    params.feeList.forEach((item) => {
      let trnsFee = new TransactionFee();
      trnsFee.setFeeType(item.feeType);
      trnsFee.setFee(item.fee);
      trnsFees.push(trnsFee);
    });

    req.setTransactionFeesList(trnsFees);
  }

  if (screen === 'Cash Movement') {
    if (!params.entryType) {
      notifyError('Entry Type is required.');
      return null;
    }
    if (!params.accountNumber) {
      notifyError('Account No. is required.');
      return null;
    }
    if (!params.contraAccount) {
      notifyError('Contra Account is required.');
      return null;
    }
    if (!params.netAmount && !params.localNetAmt) {
      notifyError('Net Amount or Local Net Amount is required.');
      return null;
    }

    req.setEntryType(params.entryType);
    req.setEntrySubType(params.entrySubType);
    req.setCorrespondent(params.correspondent);
    req.setAccountNo(params.accountNumber);
    req.setContraCorrespondent(params.contraCorrespondent);
    req.setContraAccountNo(params.contraAccount);
    req.setNetAmt(params.netAmount);
    req.setSettleDate(stringToProtoDate(params.settleDate));
    req.setHoldDate(stringToProtoDate(params.holdDate));
    req.setDescription(params.description);
    req.setExternalId(params.externalId);
    req.setExecutionId(params.executionId);
    req.setVendor(params.vendor);
    req.setRefId(params.refId);
    let metadata = null;
    if (params.metadata) {
      metadata = validateMetadata(params.metadata);
      if (!metadata) return null;
      req.setMetadata(metadata);
    }
    if (
      !validateEntryTypeMetadata(
        params.entryType,
        params.entrySubType,
        params.metadata
      )
    ) {
      return null;
    }

    if (params.contraSymbol && params.contraSymbol !== 'USD') {
      req.setContraSymbol(
        params.contraSymbol.startsWith('$')
          ? params.contraSymbol
          : '$' + params.contraSymbol
      );

      if (ENTRY_TYPES_AUTOFILL_BLACKLIST.includes(params.entryType) && !params.swapRate) {
        notifyError('Swap Rate is required.');
        return null;
      }

      req.setSwapRate(params.swapRate);
      req.setSwapRateAt(stringToProtoTradeTimeStamp(params.swapRateAt));
      req.setLocalNetAmt(params.localNetAmt);
    }

    if (params.entryType === 'INVPNL') {
      if (!params.symbol && !hasIsinMic(params)) {
        notifyError('Symbol is required when ISIN and MIC are not provided.');
        return null;
      }
      if (params.isin && !params.exchangeMic) {
        notifyError('Exchange MIC is required when ISIN is provided.');
        return null;
      }

      if (params.isin && !/^[A-Z]{2}[0-9A-Z]{9}[0-9]$/.test(params.isin)) {
        notifyError(
          'ISIN must be in format: 2 letters + 9 alphanumeric + 1 digit (e.g., US0378331005).'
        );
        return null;
      }

      req.setSymbol(params.symbol);
      req.setCusip(params.cusip);
      req.setIsin(params.isin);
      req.setExchangeMic(params.exchangeMic);
    }

    if (params.entryType === 'FXPNL' && params.tradeDate) {
      req.setTradeDate(stringToProtoDate(params.tradeDate));
    }
  }

  if (screen === 'Cash and/or Position Movement') {
    if (!params.entryType) {
      notifyError('Entry Type is required.');
      return null;
    }
    if (!params.accountNumber) {
      notifyError('Account No. is required.');
      return null;
    }
    if (!params.contraAccount) {
      notifyError('Contra Account is required.');
      return null;
    }
    if (params.entryType === 'CFEE') {
      if (params.symbol) {
        params.netAmount = null;
      }
      if (
        (!params.symbol && !params.netAmount) ||
        (params.symbol && params.netAmount)
      ) {
        notifyError('Cannot accept both Net Amount and Symbol filled at once.');
        return null;
      }
      if (params.symbol && (!params.price || !params.qty)) {
        notifyError('Price and Qty are required for the Symbol');
        return null;
      }
      if (params.netAmount && (params.price || params.qty)) {
        notifyError(
          'Price or Qty should not be given when Net Amount is given'
        );
        return null;
      }
    } else if (params.entryType === 'FXCNV') {
      if (!params.symbol) {
        notifyError('Symbol is required.');
        return null;
      }
      if (!params.qty || params.qty == 0) {
        notifyError('Quantity is required for FXCNV.');
        return null;
      }
      
      // For FXCNV with currency symbols, use net_amount instead of qty
      // Backend constraint: exactly one of qty or net_amt must be non-zero
      if (params.symbol && params.symbol.startsWith('$')) {
        // Currency symbol - use net_amount, clear qty
        params.netAmount = params.qty;
        params.qty = null;
      } else {
        // Regular symbol - use qty, clear net_amount
        params.netAmount = '0';
      }
    } else {
      if (!params.symbol && !hasIsinMic(params)) {
        notifyError('Symbol is required when ISIN and MIC are not provided.');
        return null;
      }
      if (!params.netAmount && !params.localNetAmt) {
        notifyError('Net Amount or Local Net Amount is required.');
        return null;
      }
    }
    // if (!params.qty && !params.netAmount) {
    //   if (!params.qty) {
    //     notifyError('Enter either QTY or Net Amount.');
    //     return;
    //   }
    //   if (!params.netAmount) {
    //     notifyError('Enter either QTY or Net Amount.');
    //     return;
    //   }
    // }
    // if (!params.price) {
    //   notifyError('Price is required.');
    //   return;
    // }

    if (params.isin && !params.exchangeMic) {
      notifyError('Exchange MIC is required when ISIN is provided.');
      return null;
    }

    if (params.isin && !/^[A-Z]{2}[0-9A-Z]{9}[0-9]$/.test(params.isin)) {
      notifyError(
        'ISIN must be in format: 2 letters + 9 alphanumeric + 1 digit (e.g., US0378331005).'
      );
      return null;
    }

    req.setEntryType(params.entryType);
    req.setEntrySubType(params.entrySubType);
    req.setCorrespondent(params.correspondent);
    req.setAccountNo(params.accountNumber);
    req.setContraCorrespondent(params.contraCorrespondent);
    req.setContraAccountNo(params.contraAccount);
    req.setSymbol(params.symbol);
    req.setCusip(params.cusip);
    req.setIsin(params.isin);
    req.setExchangeMic(params.exchangeMic);
    
    // Set qty only if it's not null (FXCNV with currency symbols sets it to null)
    if (params.qty !== null && params.qty !== undefined) {
      req.setQty(params.qty);
    }

    // For FXCNV, explicitly set net_amt to "0" or the mapped value, otherwise set the provided value
    if (params.entryType === 'FXCNV') {
      req.setNetAmt(params.netAmount || '0');
    } else {
      req.setNetAmt(params.netAmount);
    }
    
    req.setSettleDate(stringToProtoDate(params.settleDate));
    req.setHoldDate(stringToProtoDate(params.holdDate));
    req.setDescription(params.description);
    req.setContraAccountDesignator(params.contraAccountDesignator);
    req.setRefId(params.refId);
    
    // Set trade_date if provided, otherwise set executed_at to current timestamp for FXCNV
    if (params.entryType === 'FXCNV') {
      if (params.tradeDate) {
        req.setTradeDate(stringToProtoDate(params.tradeDate));
        req.setExecutedAt(stringToProtoTradeTimeStamp(params.tradeDate));
      } else {
        const now = moment().format('YYYY-M-D H:m');
        req.setExecutedAt(stringToProtoTradeTimeStamp(now));
      }
    }
    let metadata = null;
    if (params.metadata) {
      metadata = validateMetadata(params.metadata);
      if (!metadata) return null;
      req.setMetadata(metadata);
    }
    if (
      !validateEntryTypeMetadata(
        params.entryType,
        params.entrySubType,
        params.metadata
      )
    ) {
      return null;
    }

   // Set price or local_price based on currency FOR FXCNV ONLY
    if (params.entryType === 'FXCNV' && params.contraSymbol && params.contraSymbol !== 'USD') {
      req.setLocalPrice(params.price);
    } else {
      req.setPrice(params.price);
    }

    // Handle FXCNV separately - optionally set swap rate fields but NOT contra_symbol
    if (params.entryType === 'FXCNV') {
      // For FXCNV, all FX fields are optional
      if (params.swapRate) {
        req.setSwapRate(params.swapRate);
      }
      if (params.swapRateAt) {
        req.setSwapRateAt(stringToProtoTradeTimeStamp(params.swapRateAt));
      }
      if (params.localNetAmt) {
        req.setLocalNetAmt(params.localNetAmt);
      }
    } else {
      // For non-FXCNV entry types, set contra_symbol and related fields
      if (params.contraSymbol && params.contraSymbol !== 'USD') {
        req.setContraSymbol(
          params.contraSymbol.startsWith('$')
            ? params.contraSymbol
            : '$' + params.contraSymbol
        );

        if (ENTRY_TYPES_AUTOFILL_BLACKLIST.includes(params.entryType) && !params.swapRate) {
          notifyError('Swap Rate is required.');
          return null;
        }

        req.setSwapRate(params.swapRate);
        req.setSwapRateAt(stringToProtoTradeTimeStamp(params.swapRateAt));
        req.setLocalNetAmt(params.localNetAmt);
      }
    }
  }

  if (screen === 'Position Movement') {
    if (!params.entryType) {
      notifyError('Entry Type is required.');
      return null;
    }
    if (!params.accountNumber) {
      notifyError('Account No. is required.');
      return null;
    }
    if (!params.contraAccount) {
      notifyError('Contra Account is required.');
      return null;
    }
    if (!params.symbol && !hasIsinMic(params)) {
      notifyError('Symbol is required when ISIN and MIC are not provided.');
      return null;
    }
    if (!params.qty) {
      notifyError('Quantity is required.');
      return null;
    }
    if (!params.price && params.entryType !== 'MMSWP') {
      notifyError('Price is required.');
      return null;
    }

    if (params.isin && !params.exchangeMic) {
      notifyError('Exchange MIC is required when ISIN is provided.');
      return null;
    }

    if (params.isin && !/^[A-Z]{2}[0-9A-Z]{9}[0-9]$/.test(params.isin)) {
      notifyError(
        'ISIN must be in format: 2 letters + 9 alphanumeric + 1 digit (e.g., US0378331005).'
      );
      return null;
    }

    req.setEntryType(params.entryType);
    req.setEntrySubType(params.entrySubType);
    req.setCorrespondent(params.correspondent);
    req.setAccountNo(params.accountNumber);
    req.setContraCorrespondent(params.contraCorrespondent);
    req.setContraAccountNo(params.contraAccount);
    req.setSymbol(params.symbol);
    req.setCusip(params.cusip);
    req.setIsin(params.isin);
    req.setExchangeMic(params.exchangeMic);
    req.setQty(params.qty);

    req.setSettleDate(stringToProtoDate(params.settleDate));
    req.setHoldDate(stringToProtoDate(params.holdDate));
    req.setDescription(params.description);
    req.setContraAccountDesignator(params.contraAccountDesignator);
    req.setRefId(params.refId);
    req.setPrice(params.price);

    if (params.contraSymbol && params.contraSymbol !== 'USD') {
      req.setContraSymbol(
        params.contraSymbol.startsWith('$')
          ? params.contraSymbol
          : '$' + params.contraSymbol
      );

      if (ENTRY_TYPES_AUTOFILL_BLACKLIST.includes(params.entryType) && !params.swapRate) {
        notifyError('Swap Rate is required.');
        return null;
      }

      req.setSwapRate(params.swapRate);
      req.setSwapRateAt(stringToProtoTradeTimeStamp(params.swapRateAt));
    }
    if (params.metadata) {
      let metadata = validateMetadata(params.metadata);
      if (!metadata) return null;
      req.setMetadata(metadata);
    }
    if (
      !validateEntryTypeMetadata(
        params.entryType,
        params.entrySubType,
        params.metadata
      )
    ) {
      return null;
    }
  }

  try {
    req = handleJournalPositionCash(screen, params, req);
  } catch (err) {
    notifyError(err.message);
    return null;
  }

  return req;
}

function validateMetadata(metadataStr) {
  if (metadataStr) {
    try {
      return stringToProtoTransactionMetadata(metadataStr);
    } catch (e) {
      notifyError(e.message);
      return;
    }
  }
}

function handleJournalPositionCash(screen, params, req) {
  if (screen === 'Journal Position and Cash') {
    if (!params.entryType) {
      throw new Error('Entry Type is required.');
    }
    if (!params.accountNumber) {
      throw new Error('Account No. is required.');
    }
    if (!params.contraAccount) {
      throw new Error('Contra Account is required.');
    }
    if (!params.symbol && !hasIsinMic(params)) {
      throw new Error('Symbol is required when ISIN and MIC are not provided.');
    }
    if (!params.qty) {
      throw new Error('Quantity is required.');
    }
    if (!params.netAmount) {
      throw new Error('Net Amount is required.');
    }

    if (params.isin && !params.exchangeMic) {
      throw new Error('Exchange MIC is required when ISIN is provided.');
    }

    if (params.isin && !/^[A-Z]{2}[0-9A-Z]{9}[0-9]$/.test(params.isin)) {
      throw new Error(
        'ISIN must be in format: 2 letters + 9 alphanumeric + 1 digit (e.g., US0378331005).'
      );
    }

    req.setEntryType(params.entryType);
    req.setCorrespondent(params.correspondent);
    req.setAccountNo(params.accountNumber);
    req.setContraCorrespondent(params.contraCorrespondent);
    req.setContraAccountNo(params.contraAccount);
    req.setSymbol(params.symbol);
    req.setCusip(params.cusip);
    req.setIsin(params.isin);
    req.setExchangeMic(params.exchangeMic);
    req.setQty(params.qty);

    req.setNetAmt(params.netAmount);
    req.setExecutedAt(stringToProtoTradeTimeStamp(params.tradeDate));
    req.setSettleDate(stringToProtoDate(params.settleDate));
    req.setDescription(params.description);
    req.setExecutionId(params.executionId);
    req.setExternalId(params.externalId);
    req.setExecutingVenue(params.executingVenue);
    req.setOrderId(params.orderId);
    req.setLeavesQty(params.leavesQty);
    req.setContraAccountDesignator(params.contraAccountDesignator);
    req.setRefId(params.refId);

        if (params.contraSymbol && params.contraSymbol !== 'USD') {
          req.setContraSymbol(
            params.contraSymbol.startsWith('$')
              ? params.contraSymbol
              : '$' + params.contraSymbol
          );
        }
  }
  return req;
}

function keyRandomizer() {
  return Math.random()
    .toString(36)
    .substring(7);
}

// True when the user has fully identified the security via ISIN + MIC.
// In that case, symbol/cusip are not required by the UI.
const hasIsinMic = (params) => {
  return Boolean(params?.isin) && Boolean(params?.exchangeMic);
};

export default function TransactionForm({ trnsid, accountid, actionType }) {
  const profile = useProfile();
  const classes = useStyles();
  const confirm = useConfirm();
  const showBodWarning = useBodRiskWindow();
  const [isCancelAndCorrect, setIsCancelAndCorrect] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [isValidating, setIsValidating] = useState(false);

  const [screen, setScreen] = useState(1);
  const [screenDescription, setScreenDescription] = useState('Trade');
  const [transactionValue, setTransactionValue] = useState('TRD');
  const [entrySubTypeValue, setEntrySubTypeValue] = useState('');
  const [sideValue, setSideValue] = useState('');
  const [correspondentValue, setCorrespondentValue] = useState('');
  const [accountNumberValue, setAccountNumberValue] = useState('');
  const [contraCorrespondentValue, setContraCorrespondentValue] = useState('');
  const [contraAccountValue, setContraAccountValue] = useState('');
  const [symbolValue, setSymbolValue] = useState('');
  const [cusipValue, setCusipValue] = useState('');
  const [isinValue, setIsinValue] = useState('');
  const [exchangeMicValue, setExchangeMicValue] = useState('');
  const [qtyValue, setQtyValue] = useState('');
  const [priceValue, setPriceValue] = useState('');
  const [grossAmountValue, setGrossAmountValue] = useState('');
  const [netAmountValue, setNetAmountValue] = useState('');
  const [executedAtValue, setExecutedAtValue] = useState(
    profile.systemDate.format('YYYY-MM-DDTh:mm')
  );
  const [tradeDateValue, setTradeDateValue] = useState(
    profile.tradeDate.format('YYYY-MM-DD')
  );
  const [settleDateValue, setSettleDateValue] = useState(
    profile.settleDate.format('YYYY-MM-DD')
  );
  const [descriptionValue, setDescriptionValue] = useState('');
  const [executionIdValue, setExecutionIdValue] = useState('');
  const [externalIdValue, setExternalIdValue] = useState('');
  const [executingVenueValue, setExecutingVenueValue] = useState('');
  const [vendorValue, setVendorValue] = useState('');
  const [orderIdValue, setOrderIdValue] = useState('');
  const [leavesQtyValue, setLeavesQtyValue] = useState('');
  const [
    contraAccountDesignatorValue,
    setContraAccountDesignatorValue,
  ] = useState('');
  const [refIdValue, setRefIdValue] = useState('');
  const [holdDateValue, setHoldDateValue] = useState('');
  const [metadataValue, setMetadataValue] = useState('');
  const [contraSymbolValue, setContraSymbolValue] = useState('');
  const [swapRateValue, setSwapRateValue] = useState('');
  const [swapRateAtValue, setSwapRateAtValue] = useState('');
  const [localGrossAmtValue, setLocalGrossAmtValue] = useState('');
  const [localNetAmtValue, setLocalNetAmtValue] = useState('');
  const [swapFeeBpsAlpaValue, setSwapFeeBpsAlpaValue] = useState('');
  const [swapFeeBpsCorrValue, setSwapFeeBpsCorrValue] = useState('');
  const [localFeesValue, setLocalFeesValue] = useState('');

  const [isDisabled, setIsDisabled] = useState(false);
  const [usdFieldsDisabled, setUsdFieldsDisabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({
    severity: 'success',
    message: '',
  });

  const [feesValue, setFeesValue] = useState(0);
  const [feeListValue, setFeeListValue] = useState([
    { feeType: 'TAF Fee', fee: '0' },
    { feeType: 'REG Fee', fee: '0' },
  ]);
  const [openImport, setOpenImport] = React.useState(false);

  const handleUploadClose = async (data, isEdit) => {
    setOpenImport(false);
  };

  const {
    transactionServiceClient: transactionClient,
    pendingTrnsServiceClient: pendingtrnsClient,
  } = useContext(ServiceContext);

  useEffect(() => {
    if (actionType !== 'process-pending') {
      setMessage({
        severity: 'success',
        message: '',
      });
    }
  }, [actionType]);

  useEffect(() => {
    let total = 0;
    feeListValue.forEach((item) => {
      total += parseFloat(item.fee);
    });
    setFeesValue(total);
  }, [feeListValue]);

  useEffect(() => {
    if (sideValue) {
      calculateRegFee(grossAmountValue);
      calculateTaf(qtyValue);
    } // eslint-disable-next-line
  }, [sideValue]);

  useEffect(() => {
    let req = new ListFeesRequest();

    transactionClient.listFees(req, meta, (err, res) => {
      if (err) {
        notifyError(err.message);
        return;
      }

      let newArr = feeListValue.map((item) => {
        return item;
      });

      res.toObject().feesList.forEach((fee) => {
        newArr.push({ feeType: fee.feeType, fee: '0' });
      });
      setFeeListValue(newArr);
    }); // eslint-disable-next-line
  }, []);

  const inputs = [
    {
      name: 'correspondent',
      type: 'autocomplete-correspondent',
      label: 'Correspondent',
      required: false,
    },
    {
      name: 'account_number',
      type: 'autocomplete-accountno',
      label: 'Account No',
      required: true,
    },
    {
      name: 'vendor',
      type: 'autocomplete-vendor',
      label: 'Vendor',
      required: true,
    },
    {
      name: 'contra_correspondent',
      type: 'autocomplete-contra-correspondent',
      label: 'Contra Correspondent',
      required: false,
    },
    {
      name: 'symbol',
      type: 'autocomplete-symbol',
      label: 'Symbol',
      required: !(isinValue && exchangeMicValue),
    },
    {
      name: 'cusip',
      type: 'autocomplete-cusip',
      label: 'Cusip',
      required: !(isinValue && exchangeMicValue),
    },
    {
      name: 'isin',
      type: 'text',
      label: 'ISIN',
      required: false,
    },
    {
      name: 'exchangeMic',
      type: 'text',
      label: 'MIC',
      required: isinValue ? true : false,
    },
    {
      name: 'contra_account',
      type: 'autocomplete-contraaccount',
      label: 'Contra Account',
      required: true,
    },
    {
      name: 'side',
      type: 'select',
      label: 'Side',
      required: true,
    },
    {
      name: 'qty',
      type: 'number',
      label: 'QTY',
      required: false,
    },
    {
      name: 'price',
      type: 'number',
      label: 'Price',
      required: false,
    },
    {
      name: 'gross_amount',
      type: 'number',
      label: 'Gross Amount',
      required: false,
      disabled:
        isDisabled ||
        (usdFieldsDisabled && (contraSymbolValue || 'USD') !== 'USD'),
    },
    {
      name: 'fees',
      type: 'multiple-fees',
      label: 'Fees',
      required: false,
    },
    {
      name: 'net_amount',
      type: 'number',
      label: 'Net Amount',
      required: false,
      disabled:
        isDisabled ||
        (usdFieldsDisabled && (contraSymbolValue || 'USD') !== 'USD'),
    },
    {
      name: 'executed_at',
      type: 'time',
      label: 'Trade Executed At (ET)',
      required: true,
    },
    {
      name: 'trade_date',
      type: 'date',
      label: 'Trade Date',
      required: false,
    },
    {
      name: 'settle_date',
      type: 'date',
      label: 'Settle Date',
      required: false,
    },
    {
      name: 'description',
      type: 'text',
      label: 'Description',
      required: false,
    },
    {
      name: 'entry_type',
      type: 'select',
      label: 'Entry Type',
      required: true,
    },
    {
      name: 'entry_sub_type',
      type: 'autocomplete-entry-sub-type',
      label: 'Entry Sub Type',
      required: false,
    },
    {
      name: 'execution_id',
      type: 'text',
      label: 'Execution ID',
      required: false,
    },
    {
      name: 'external_id',
      type: 'text',
      label: 'External ID',
      required: false,
    },
    {
      name: 'executing_venue',
      type: 'text',
      label: 'Executing Venue',
      required: false,
    },
    {
      name: 'order_id',
      type: 'text',
      label: 'Order ID',
      required: false,
    },
    {
      name: 'leaves_qty',
      type: 'number',
      label: 'Leaves Qty',
      required: false,
    },
    {
      name: 'contra_account_designator',
      type: 'custom-select',
      label: 'Contra Account Designator',
      required: false,
    },
    {
      name: 'ref_id',
      type: 'text',
      label: 'Ref ID',
      required: false,
    },
    {
      name: 'hold_date',
      type: 'date',
      label: 'Hold Date',
      required: false,
    },
    {
      name: 'metadata',
      type: 'text',
      label: 'Metadata',
      required: false,
    },
    {
      name: 'contra_symbol',
      type: 'autocomplete-contra-symbol',
      label: 'Currency',
      required: false,
    },
    {
      name: 'swap_rate',
      type: 'number',
      label: contraSymbolValue && contraSymbolValue !== 'USD'
        ? `Swap Rate (${(contraSymbolValue || '').replace('$', '')} / USD)`
        : 'Swap Rate',
      required: false,
    },
    {
      name: 'swap_rate_at',
      type: 'time',
      label: 'Swap Rate At',
      required: false,
    },
    {
      name: 'local_gross_amt',
      type: 'number',
      label: 'Local Gross Amount',
      required: false,
      disabled: isDisabled,
    },
    {
      name: 'local_net_amt',
      type: 'number',
      label: contraSymbolValue
        ? `Local Net Amount (${contraSymbolValue})`
        : 'Local Net Amount',
      required: false,
      disabled: isDisabled,
    },
    {
      name: 'swap_fee_bps_alpa',
      type: 'number',
      label: 'Swap Fee BPS Alpaca',
      required: false,
    },
    {
      name: 'swap_fee_bps_corr',
      type: 'number',
      label: 'Swap Fee BPS Correspondent',
      required: false,
    },
    {
      name: 'local_fees',
      type: 'number',
      label: contraSymbolValue
        ? `Local Fees (${contraSymbolValue}) - Subledger Only`
        : 'Local Fees - Subledger Only',
      required: false,
    },
  ];

  useEffect(() => {
    if (isPending) {
      handleSubmit();
      setIsPending(false);
    } // eslint-disable-next-line
  }, [isPending]);

  useEffect(() => {
    //set default to velox if selected entry type is TRD
    if (transactionValue === 'TRD' && !trnsid) {
      setContraAccountValue('1115');
    }

    // Disable USD net_amt input for JNLC, CSD and CSW as the amount will
    // be kept as non-USD til trade happened, and local_net_amt is more
    // important before that.
    if (
      transactionValue === 'JNLC' ||
      transactionValue === 'CSD' ||
      transactionValue === 'CSW'
    ) {
      setUsdFieldsDisabled(true);
    } else {
      setUsdFieldsDisabled(false);
    }

    //disabled gross amount and net amount if selected screen is Trade
    if (screenDescription === 'Trade') {
      setIsDisabled(true);
    } else {
      setIsDisabled(false);
    }
  }, [transactionValue, screenDescription, trnsid]);

  useEffect(() => {
    if (trnsid) {
      let readReq;

      if (actionType === 'process-pending') {
        readReq = new ReadPendingTrnsRequest();
        readReq.setStageId(trnsid);
      } else {
        setIsCancelAndCorrect(true);

        readReq = new ReadTransactionRequest();

        readReq.setTrnsId(trnsid);
        readReq.setAccountId(accountid);
      }

      setLoading(true);
      (actionType === 'process-pending'
        ? pendingtrnsClient
        : transactionClient
      ).readTransaction(readReq, {}, (err, res) => {
        console.log(err);
        if (err) {
          notifyError(err.message);
          setMessage({
            severity: 'error',
            message: err.message,
          });
          setLoading(false);
          return;
        }

        const trns = res.toObject();
        if (
          trns.pendingTransaction &&
          trns.pendingTransaction.status === 'correct'
        ) {
          setMessage({
            severity: 'success',
            message: 'Transaction is already corrected.',
          });
          //notifySuccess('Transaction is already corrected.');
        }

        if (err) {
          notifyError(err.message);
          return;
        }
        let data =
          actionType === 'process-pending'
            ? trns.pendingTransaction
            : trns.transaction;

        const targetScreen = trnsOptions.find(
          (o) => o.value === data.entryType
        );
        if (!targetScreen) {
          notifyError(`unknown entry type ${data.entryType}`);
          return;
        } else {
          setScreen(targetScreen.screen_no);
          setScreenDescription(targetScreen.screen);
        }
        setTransactionValue(data.entryType);
        setEntrySubTypeValue(data.entrySubType);
        setCorrespondentValue(data.correspondent);
        setAccountNumberValue(data.accountNo);
        setContraCorrespondentValue(data.contraCorrespondent);
        setContraAccountValue(data.contraAccountNo);
        setExecutionIdValue(data.executionId);
        setExternalIdValue(data.externalId);
        setSideValue(data.side);
        setSymbolValue(data.symbol);
        setCusipValue(data.cusip);
        setIsinValue(data.isin);
        setExchangeMicValue(data.exchangeMic);
        setQtyValue(data.qty);
        setPriceValue(data.price);
        setGrossAmountValue(data.grossAmt);
        setFeesValue(data.fees);
        setNetAmountValue(data.netAmt);
        setExecutedAtValue(
          formatTimestampForInput(data.executedAt || data.tradeAt)
        );
        setTradeDateValue(formatProtoDateForInput(data.tradeDate));
        setSettleDateValue(formatProtoDateForInput(data.settleDate));
        setHoldDateValue(formatProtoDateForInput(data.holdDate));
        setDescriptionValue(data.description);
        setExecutingVenueValue(data.executionVenue || data.executingVenue);
        setVendorValue(data.vendor);
        setOrderIdValue(data.orderId);
        setLeavesQtyValue(data.leavesQty);
        setContraAccountDesignatorValue(data.contraAccountDesignator);
        setRefIdValue(data.refId);
        let resTrns =
          actionType === 'process-pending'
            ? res.getPendingTransaction()
            : res.getTransaction();
        let fmtedMetadata = protoTransactionMetadataToString(
          resTrns.getMetadata()
        );
        setMetadataValue(fmtedMetadata);
        setContraSymbolValue((data.contraSymbol || '').replace('$', ''));
        // Display swap rate as reciprocal for cash movement entry types
        if (RECIPROCAL_ENTRY_TYPES.includes(data.entryType)) {
          setSwapRateValue(
            data.swapRate && parseFloat(data.swapRate) !== 0
              ? (1 / parseFloat(data.swapRate)).toString()
              : data.swapRate
          );
        } else {
          setSwapRateValue(data.swapRate);
        }
        setSwapRateAtValue(formatTimestampForInput(data.swapRateAt));
        setLocalGrossAmtValue(data.localGrossAmt);
        setLocalNetAmtValue(data.localNetAmt);
        setSwapFeeBpsAlpaValue(data.swapFeeBpsAlpa);
        setSwapFeeBpsCorrValue(data.swapFeeBpsCorr);
        setLocalFeesValue(
          protoTransactionMetadataLocalFees(resTrns.getMetadata())
        );

        // cleaning up usd value when non-USD for special cash movements
        if (
          data.entryType === 'JNLC' ||
          data.entryType === 'CSD' ||
          data.entryType === 'CSW'
        ) {
          setNetAmountValue(
            (data.contraSymbol || 'USD') === 'USD' ? data.netAmt : ''
          );
        }

        setLoading(false);
      });
    } else {
      setIsCancelAndCorrect(false);
    } // eslint-disable-next-line
  }, [
    transactionClient,
    pendingtrnsClient,
    trnsid,
    accountid,
    actionType,
  ]);

  const handleOnChangeType = (event) => {
    const selectedValue = event.target.value;
    const isTrns = some(trnsOptions, ['value', selectedValue]);
    const isEntrySubType = some(entrySubTypeOptions, ['value', selectedValue]);
    let targetValue;

    if (isTrns) {
      /***Note Refactor Me *******/
      targetValue = trnsOptions.find((o) => o.value === event.target.value);
      setScreen(targetValue.screen_no);
      setScreenDescription(targetValue.screen);
      setTransactionValue(selectedValue);
      setEntrySubTypeValue('');
      setSideValue('');
      setCorrespondentValue('');
      setAccountNumberValue('');
      setContraCorrespondentValue('');
      setContraAccountValue('');
      setSymbolValue('');
      setCusipValue('');
      setIsinValue('');
      setExchangeMicValue('');
      setDescriptionValue('');
      setQtyValue('');
      setPriceValue('');
      setGrossAmountValue('');
      setFeesValue('');
      setNetAmountValue('');
      setTradeDateValue('');
      setSettleDateValue('');
      setHoldDateValue('');
      setExecutionIdValue('');
      setExternalIdValue('');
      setExecutingVenueValue('');
      setVendorValue('');
      setOrderIdValue('');
      setLeavesQtyValue('');
      setContraAccountDesignatorValue('');
      setRefIdValue('');
      setMetadataValue('');
      setContraSymbolValue('');
      setSwapRateValue('');
      setSwapRateAtValue('');
      setLocalGrossAmtValue('');
      setLocalNetAmtValue('');
      setLocalFeesValue('');
      setSwapFeeBpsAlpaValue('');
      setSwapFeeBpsCorrValue('');
      /***Note: Clean Me up*******/
      document.getElementById('transaction-form').reset();
    } else if (isEntrySubType) {
      setEntrySubTypeValue(selectedValue);
    } else {
      setSideValue(selectedValue);
    }

    if (selectedValue === 'TRD') {
      setContraAccountValue('1115');
    }
  };

  const getTransactionParams = () => {
    if (screenDescription === 'Trade') {
      return {
        entryType: transactionValue,
        entrySubType: entrySubTypeValue,
        correspondent: correspondentValue,
        accountNumber: accountNumberValue,
        contraCorrespondent: contraCorrespondentValue,
        contraAccount: contraAccountValue,
        side: sideValue,
        symbol: symbolValue,
        cusip: cusipValue,
        isin: isinValue,
        exchangeMic: exchangeMicValue,
        qty: qtyValue,
        price: priceValue,
        grossAmount: grossAmountValue,
        fees: feesValue,
        netAmount: netAmountValue,
        executedAt: executedAtValue,
        tradeDate: tradeDateValue,
        settleDate: settleDateValue,
        description: descriptionValue,
        executionId: executionIdValue,
        externalId: externalIdValue,
        executingVenue: executingVenueValue,
        orderId: orderIdValue,
        leavesQty: leavesQtyValue,
        feeList: feeListValue,
        contraAccountDesignator: contraAccountDesignatorValue,
        refId: refIdValue,
        metadata: metadataValue,
        contraSymbol: contraSymbolValue,
        swapRate: convertSwapRateForBackend(swapRateValue, transactionValue),
        swapRateAt: swapRateAtValue,
        localGrossAmt: localGrossAmtValue,
        localNetAmt: localNetAmtValue,
        swapFeeBpsAlpa: swapFeeBpsAlpaValue,
        swapFeeBpsCorr: swapFeeBpsCorrValue,
        localFees: localFeesValue,
      };
    }

    if (screenDescription === 'Cash Movement') {
      return {
        entryType: transactionValue,
        entrySubType: entrySubTypeValue,
        correspondent: correspondentValue,
        accountNumber: accountNumberValue,
        contraCorrespondent: contraCorrespondentValue,
        contraAccount: contraAccountValue,
        netAmount: netAmountValue,
        tradeDate: tradeDateValue,
        settleDate: settleDateValue,
        holdDate: holdDateValue,
        description: descriptionValue,
        executionId: executionIdValue,
        externalId: externalIdValue,
        vendor: vendorValue,
        symbol: symbolValue,
        cusip: cusipValue,
        isin: isinValue,
        exchangeMic: exchangeMicValue,
        refId: refIdValue,
        metadata: metadataValue,
        contraSymbol: contraSymbolValue,
        swapRate: convertSwapRateForBackend(swapRateValue, transactionValue),
        swapRateAt: swapRateAtValue,
        localNetAmt: localNetAmtValue,
      };
    }

    if (screenDescription === 'Cash and/or Position Movement') {
      return {
        entryType: transactionValue,
        entrySubType: entrySubTypeValue,
        correspondent: correspondentValue,
        accountNumber: accountNumberValue,
        contraCorrespondent: contraCorrespondentValue,
        contraAccount: contraAccountValue,
        symbol: symbolValue,
        cusip: cusipValue,
        isin: isinValue,
        exchangeMic: exchangeMicValue,
        qty: qtyValue,
        price: priceValue,
        netAmount: netAmountValue,
        tradeDate: tradeDateValue,
        settleDate: settleDateValue,
        holdDate: holdDateValue,
        description: descriptionValue,
        refId: refIdValue,
        metadata: metadataValue,
        contraSymbol: contraSymbolValue,
        swapRate: convertSwapRateForBackend(swapRateValue, transactionValue),
        swapRateAt: swapRateAtValue,
        localNetAmt: localNetAmtValue,
      };
    }

    if (screenDescription === 'Position Movement') {
      return {
        entryType: transactionValue,
        entrySubType: entrySubTypeValue,
        correspondent: correspondentValue,
        accountNumber: accountNumberValue,
        contraCorrespondent: contraCorrespondentValue,
        contraAccount: contraAccountValue,
        symbol: symbolValue,
        cusip: cusipValue,
        isin: isinValue,
        exchangeMic: exchangeMicValue,
        qty: qtyValue,
        price: priceValue,
        settleDate: settleDateValue,
        holdDate: holdDateValue,
        description: descriptionValue,
        contraAccountDesignator: contraAccountDesignatorValue,
        refId: refIdValue,
        metadata: metadataValue,
        contraSymbol: contraSymbolValue,
        swapRate: convertSwapRateForBackend(swapRateValue, transactionValue),
        swapRateAt: swapRateAtValue,
      };
    }

    if (screenDescription === 'Journal Position and Cash') {
      return {
        entryType: transactionValue,
        correspondent: correspondentValue,
        accountNumber: accountNumberValue,
        contraCorrespondent: contraCorrespondentValue,
        contraAccount: contraAccountValue,
        symbol: symbolValue,
        cusip: cusipValue,
        isin: isinValue,
        exchangeMic: exchangeMicValue,
        qty: qtyValue,
        netAmount: netAmountValue,
        price: priceValue,
        tradeDate: tradeDateValue,
        settleDate: settleDateValue,
        description: descriptionValue,
        executionId: executionIdValue,
        externalId: externalIdValue,
        executingVenue: executingVenueValue,
        orderId: orderIdValue,
        leavesQty: leavesQtyValue,
        contraAccountDesignator: contraAccountDesignatorValue,
        refId: refIdValue,
        metadata: metadataValue,
      };
    }

    return {};
  };

  const handleSubmit = async () => {
    const params = getTransactionParams();

    if (isCancelAndCorrect && isBodRiskWindow()) {
      try {
        await confirm({
          description: cancelConfirmDescription(
            'You are about to cancel and correct this transaction, please confirm.'
          ),
          confirmationText: 'Proceed anyway',
          cancellationText: 'Cancel',
        });
      } catch {
        return;
      }
    }

    if (affectsShortLeg(params.metadata, params.qty)) {
      try {
        await confirm({
          description:
            'For an unnetted account, this To Open and QTY combination affects the short leg (CAD 3). Depending on the existing position, it may open, extend, close, or over-close that leg. Please confirm.',
          confirmationText: 'Confirm short-leg transaction',
          cancellationText: 'Cancel',
        });
      } catch {
        return;
      }
    }

    setWaiting(true);
    try {
      await saveTransaction(
        transactionClient,
        params,
        trnsid,
        screenDescription,
        isCancelAndCorrect,
        isPending,
        actionType,
        setMessage,
      );
    } catch (err) {
      // We still notify here so rejections or throws from unhandled code are not ignored.
      // Duplicate messages will be deduped by notifyError via toastId
      notifyError(err.message);
    } finally {
      setWaiting(false);
    }
  };

  const handleValidate = async () => {
    setIsValidating(true);

    const params = getTransactionParams();
    const transactionReq = buildTransactionRequest(params, screenDescription);

    if (!transactionReq) {
      setIsValidating(false);
      return;
    }

    const validateReq = new ValidateTransactionsRequest();
    validateReq.setTransactionsList([transactionReq]);

    transactionClient.validateTransactions(validateReq, meta, (err, res) => {
      setIsValidating(false);
      if (err) {
        notifyError(err.message);
        return;
      }

      const errors = res.toObject().errorsList;
      if (!errors || errors.length == 0) {
        notifyError('Unexpected ValidateTransactionsResponse');
      }

      // Since only one transaction is being validated, just check the first element of results
      if (errors[0] != '') {
        notifyError(errors[0]);
      } else {
        notifySuccess('Validation passed successfully.');
      }
    });
  };

  function getTransactionForm(elements, screenNo) {
    const renderRowWrapper = elements.map((itemsRow) =>
      itemsRow !== null ? (
        <div
          key={itemsRow}
          className={
            itemsRow.length > 2 ? classes.grdRow : classes.grdRowInlineFlex
          }
        >
          {getFormFields(itemsRow, screenNo)}{' '}
        </div>
      ) : null
    );

    if (loading) {
      return <Typography variant="h5">Loading transaction...</Typography>;
    }

    return (
      <Box className={classes.container}>
        <form id="transaction-form">
          <div className={classes.containerTitle}>
            <div className={classes.grdRow}>
              <div className={classes.grdCell1}>
                <Typography variant="h4" className={classes.textBold}>
                  {screenDescription}
                </Typography>
              </div>
              {actionType !== 'process-pending' && (
                <div className={classes.grdCellNone}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={<ImportIcon />}
                    onClick={() => {
                      setOpenImport(true);
                    }}
                  >
                    Import
                  </Button>
                </div>
              )}
            </div>
            <br />
            {renderRowWrapper}
          </div>
          {isCancelAndCorrect && showBodWarning && (
            <div className={classes.grdRow}>
              <div className={classes.grdCell1}>
                <Alert severity="warning">{BOD_CANCEL_WARNING}</Alert>
              </div>
            </div>
          )}
          <div className={classes.grdRow}>
            <div className={classes.grdCellNone} style={{ marginRight: 10 }}>
              {message.message ? (
                <Alert severity={message.severity}>{message.message}</Alert>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={
                    waiting && !isPending ? (
                      <CircularProgress
                        style={{ color: '#ffffff', height: 20, width: 20 }}
                      />
                    ) : (
                      <span></span>
                    )
                  }
                  onClick={() => (waiting ? {} : handleSubmit())}
                  disabled={waiting}
                >
                  {waiting && !isPending
                    ? 'Processing'
                    : actionType === 'process-pending'
                    ? 'Process'
                    : 'Save'}
                </Button>
              )}
            </div>
            {actionType !== 'process-pending' && (
              <div className={classes.grdCellNone} style={{ marginRight: 10 }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={
                    waiting && isPending ? (
                      <CircularProgress
                        style={{ color: '#ffffff', height: 20, width: 20 }}
                      />
                    ) : (
                      <span></span>
                    )
                  }
                  onClick={() => setIsPending(true)}
                  disabled={waiting}
                >
                  {waiting && isPending ? 'Processing' : 'Save to Pending'}
                </Button>
              </div>
            )}
            <div className={classes.grdCell1}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={
                  isValidating ? (
                    <CircularProgress
                      style={{ color: '#ffffff', height: 20, width: 20 }}
                    />
                  ) : (
                    <span></span>
                  )
                }
                onClick={() => (isValidating ? {} : handleValidate())}
                disabled={isValidating || waiting}
              >
                {isValidating ? 'Validating' : 'Validate'}
              </Button>
            </div>
          </div>
        </form>
        {openImport && (
          <TransactionUploadModal
            onClose={handleUploadClose}
            open={openImport}
          ></TransactionUploadModal>
        )}
      </Box>
    );
  }

  function onTextInput(e, name) {
    let value = e.target.value;

    // Convert ISIN to uppercase automatically
    if (name === 'isin') {
      value = value.toUpperCase();
    }

    // Convert MIC to uppercase automatically
    if (name === 'exchangeMic') {
      value = value.toUpperCase();
    }

    if (name === 'correspondent') setCorrespondentValue(value);
    if (name === 'account_number') setAccountNumberValue(value);
    if (name === 'contra_correspondent') setContraCorrespondentValue(value);
    if (name === 'contra_account') setContraAccountValue(value);
    if (name === 'symbol') setSymbolValue(value);
    if (name === 'cusip') setCusipValue(value);
    if (name === 'isin') setIsinValue(value);
    if (name === 'exchangeMic') setExchangeMicValue(value);
    if (name === 'description') setDescriptionValue(value);
    if (name === 'execution_id') setExecutionIdValue(value);
    if (name === 'external_id') setExternalIdValue(value);
    if (name === 'executing_venue') setExecutingVenueValue(value);
    if (name === 'order_id') setOrderIdValue(value);
    if (name === 'vendor') setVendorValue(value);
    if (name === 'ref_id') setRefIdValue(value);
    if (name === 'metadata') setMetadataValue(value);
    if (name === 'contra_symbol') setContraSymbolValue(value);
  }

  function calculateTaf(qty) {
    if (
      sideValue &&
      (sideValue === 'sell' || sideValue === 'sell_short') &&
      qty !== 0
    ) {
      let req = new GetFeeRequest();
      req.setValue(qty !== '' ? qty : '0');

      transactionClient.getTafFee(req, meta, (err, res) => {
        if (err) {
          notifyError(err.message);
          return;
        }

        let val = qty !== '0' && qty !== '' ? res.toObject().fee : '0';
        updateFeeValue('taf', val);
      });
    } else {
      updateFeeValue('taf', '0');
    }
  }

  function calculateRegFee(grossAmt) {
    if (
      sideValue &&
      (sideValue === 'sell' || sideValue === 'sell_short') &&
      grossAmt !== 0
    ) {
      let req = new GetFeeRequest();
      req.setValue(grossAmt !== '' ? grossAmt : '0');

      transactionClient.getRegFee(req, meta, (err, res) => {
        if (err) {
          notifyError(err.message);
          return;
        }

        let val =
          grossAmt !== '0' && grossAmt !== '' ? res.toObject().fee : '0';
        updateFeeValue('reg', val);
      });
    } else {
      updateFeeValue('reg', '0');
    }
  }

  function onNumberInput(e, name) {
    const value = e.target.value;
    if (name === 'qty') {
      setQtyValue(value);
      calculateTaf(value);
    }
    if (name === 'price') setPriceValue(value);
    if (name === 'gross_amount') {
      setGrossAmountValue(value);
      calculateRegFee(value);
    }
    if (name === 'fees') setFeesValue(value);
    if (name === 'net_amount') setNetAmountValue(value);
    if (name === 'leaves_qty') setLeavesQtyValue(value);
    if (name === 'swap_rate') setSwapRateValue(value);
    if (name === 'local_gross_amt') setLocalGrossAmtValue(value);
    if (name === 'local_net_amt') setLocalNetAmtValue(value);
    if (name === 'swap_fee_bps_alpa') setSwapFeeBpsAlpaValue(value);
    if (name === 'swap_fee_bps_corr') setSwapFeeBpsCorrValue(value);
    if (name === 'local_fees') setLocalFeesValue(value);
  }

  function onDateInput(e, name) {
    const value = e.target.value;
    if (name === 'trade_date') setTradeDateValue(value);
    if (name === 'settle_date') setSettleDateValue(value);
    if (name === 'hold_date') setHoldDateValue(value);
  }

  function onTimeInput(e, name) {
    const value = e.target.value;
    if (name === 'executed_at') setExecutedAtValue(value);
    if (name === 'swap_rate_at') setSwapRateAtValue(value);
  }

  function getFormFields(row, screenNo) {
    let generatedRow = [];
    let count = 0;
    const screen = 'screen' + screenNo;
    row.forEach((inputField) => {
      count++;
      const rowKey = screen + count;
      const inputObject = inputs.find((o) => o.name === inputField);
      generatedRow.push(getFormField(inputObject, rowKey, row.length));
    });
    return generatedRow;
  }

  function updateFeeValue(feeType, v) {
    if (parseFloat(v) < 0) {
      return false;
    }

    let newArr = feeListValue.map((item) => {
      if (feeType === item.feeType) {
        return { ...item, fee: v };
      } else {
        return item;
      }
    });

    setFeeListValue(newArr);
  }

  function getValue(name) {
    if (name === 'correspondent') return correspondentValue;
    if (name === 'account_number') return accountNumberValue;
    if (name === 'contra_correspondent') return contraCorrespondentValue;
    if (name === 'contra_account') return contraAccountValue;
    if (name === 'symbol') return symbolValue;
    if (name === 'cusip') return cusipValue;
    if (name === 'isin') return isinValue;
    if (name === 'exchangeMic') return exchangeMicValue;
    if (name === 'description') return descriptionValue;
    if (name === 'qty') return qtyValue;
    if (name === 'price') return priceValue;
    if (name === 'gross_amount') return grossAmountValue;
    if (name === 'fees') return feesValue;
    if (name === 'net_amount') return netAmountValue;
    if (name === 'executed_at') return executedAtValue;
    if (name === 'trade_date') return tradeDateValue;
    if (name === 'settle_date') return settleDateValue;
    if (name === 'execution_id') return executionIdValue;
    if (name === 'external_id') return externalIdValue;
    if (name === 'executing_venue') return executingVenueValue;
    if (name === 'order_id') return orderIdValue;
    if (name === 'leaves_qty') return leavesQtyValue;
    if (name === 'vendor') return vendorValue;
    if (name === 'contra_account_designator')
      return contraAccountDesignatorValue;
    if (name === 'ref_id') return refIdValue;
    if (name === 'hold_date') return holdDateValue;
    if (name === 'metadata') return metadataValue;
    if (name === 'contra_symbol') return contraSymbolValue;
    if (name === 'swap_rate') return swapRateValue;
    if (name === 'swap_rate_at') return swapRateAtValue;
    if (name === 'local_gross_amt') return localGrossAmtValue;
    if (name === 'local_net_amt') return localNetAmtValue;
    if (name === 'swap_fee_bps_alpa') return swapFeeBpsAlpaValue;
    if (name === 'swap_fee_bps_corr') return swapFeeBpsCorrValue;
    if (name === 'local_fees') return localFeesValue;
  }

  function getFormField(inputObject, rowKey, rowCount) {
    if (!inputObject) return;

    const { name, type, label, required, disabled } = inputObject;
    const isMetadata = name === 'metadata';
    let field = '';

    switch (type) {
      case 'text':
        field = (
          <>
            <TextField
              required={required}
              label={label}
              key={name}
              fullWidth
              error={Boolean(
                (name === 'exchangeMic' && isinValue && !getValue(name)) ||
                  (name === 'isin' &&
                    getValue(name) &&
                    !/^[A-Z]{2}[0-9A-Z]{9}[0-9]$/.test(
                      String(getValue(name) || '')
                    ))
              )}
              onInput={(e) => onTextInput(e, name)}
              value={getValue(name) || ''}
              inputProps={{
                maxLength:
                  name === 'isin' ? 12 : name === 'exchangeMic' ? 4 : undefined,
                style:
                  name === 'isin' || name === 'exchangeMic'
                    ? { fontFamily: 'monospace' }
                    : undefined,
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
            {isMetadata && (
              <MetadataModal
                md={getValue(name)}
                setFormMd={onTextInput}
                entryType={transactionValue}
                entrySubType={entrySubTypeValue}
              />
            )}
          </>
        );
        break;
      case 'number':
        field = (
          <TextField
            required={required}
            disabled={disabled}
            label={label}
            key={name}
            type="number"
            fullWidth
            value={getValue(name) || ''}
            InputLabelProps={{ shrink: true }}
            onInput={(e) => onNumberInput(e, name)}
          />
        );
        break;
      case 'date':
        field = (
          <>
            <TextField
              required={required}
              fullWidth
              label={label}
              key={name}
              type="date"
              onInput={(e) => onDateInput(e, name)}
              value={getValue(name) || ''}
              className={classes.textField}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <DateInputWarning value={getValue(name) || ''} />
          </>
        );
        break;
      case 'time':
        field = (
          <>
            <TextField
              required={required}
              fullWidth
              label={label}
              key={name}
              type="datetime-local"
              onInput={(e) => onTimeInput(e, name)}
              value={getValue(name) || ''}
              className={classes.textField}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <DateInputWarning value={getValue(name) || ''} />
          </>
        );
        break;
      case 'select':
        {
          let options = [];
          let value = '';

          if (name === 'entry_type') {
            options = trnsOptions;
            value = transactionValue;
          }

          if (name === 'entry_sub_type') {
            options = entrySubTypeOptions;
            value = entrySubTypeValue;
          }

          if (name === 'side') {
            options = sideOptions;
            value = sideValue;
          }

          field = (
            <>
              <InputLabel shrink id={name}>
                {label + (required ? ' *' : '')}
              </InputLabel>
              <Select
                displayEmpty
                fullWidth
                key={name}
                onChange={handleOnChangeType}
                value={value || ''}
              >
                <MenuItem value="">
                  <em></em>
                </MenuItem>
                {options.map(function(item) {
                  let val = '';
                  if (
                    name === 'entry_type' ||
                    name === 'entry_sub_type' ||
                    name === 'side'
                  )
                    val = item.value;
                  if (name === 'correspondent') val = item.correspondent;
                  if (name === 'account_number') val = item.accountNo;
                  if (name === 'contra_correspondent')
                    val = item.contraCorrespondent;

                  if (!val) return '';

                  return (
                    <MenuItem key={keyRandomizer()} value={val || ''}>
                      {val}
                    </MenuItem>
                  );
                })}
              </Select>
            </>
          );
        }
        break;
      case 'autocomplete-entry-sub-type':
        field = (
          <SelectEntrySubType
            freeSolo={true}
            type="entry_sub_type"
            name={name}
            label={label}
            value={getValue(name) || ''}
            entryType={transactionValue}
            onChange={(e) => {
              setEntrySubTypeValue(e.currentTarget.value);
            }}
          />
        );
        break;
      case 'autocomplete-correspondent':
        field = (
          <SelectAccount
            freeSolo={true}
            type="correspondent"
            name={name}
            label={label}
            value={getValue(name) || ''}
            onChange={(e) => {
              setCorrespondentValue(e.currentTarget.value);
            }}
          />
        );
        break;
      case 'autocomplete-contra-correspondent':
        field = (
          <SelectAccount
            freeSolo={true}
            type="correspondent"
            name={name}
            label={label}
            value={getValue(name) || ''}
            onChange={(e) => {
              setContraCorrespondentValue(e.currentTarget.value);
            }}
          />
        );
        break;
      case 'autocomplete-accountno':
        field = (
          <SelectAccount
            freeSolo={true}
            type="accountNo"
            name={name}
            label={label}
            value={getValue(name) || ''}
            showAccountName={true}
            onChange={(e) => {
              setAccountNumberValue(e.currentTarget.value);
            }}
            selectedCorrespondent={correspondentValue ? correspondentValue : ''}
          />
        );
        break;
      case 'autocomplete-contraaccount':
        field = (
          <SelectAccount
            freeSolo={true}
            type="accountNo"
            name={name}
            label={label}
            value={getValue(name) || ''}
            showAccountName={true}
            onChange={(e) => {
              setContraAccountValue(e.currentTarget.value);
            }}
            selectedCorrespondent={
              contraCorrespondentValue ? contraCorrespondentValue : ''
            }
          />
        );
        break;
      case 'autocomplete-vendor':
        field = (
          <VendorSelect
            required={required}
            labelid={name}
            label={label}
            value={getValue(name) || ''}
            onChange={(e) => setVendorValue(e.target.value)}
            setNewValue={(event, newValue) => {
              if (newValue) {
                setVendorValue(newValue.code);
              } else {
                setVendorValue('');
                return;
              }
            }}
          />
        );
        break;
      case 'autocomplete-symbol':
        field = (
          <SelectSymbol
            freeSolo={true}
            required={required}
            labelid={name}
            label={label}
            type="symbol"
            value={getValue(name) || ''}
            onChange={(e) => setSymbolValue(e.target.value)}
            date={
              tradeDateValue
                ? moment(Date.parse(tradeDateValue)).format('yyyy-MM-DD')
                : ''
            }
          />
        );
        break;
      case 'autocomplete-cusip':
        field = (
          <SelectSymbol
            freeSolo={true}
            required={required}
            labelid={name}
            label={label}
            value={getValue(name) || ''}
            type="cusip"
            onChange={(e) => setCusipValue(e.target.value)}
            selectedSymbol={symbolValue ? symbolValue : ''}
            date={
              tradeDateValue
                ? moment(Date.parse(tradeDateValue)).format('yyyy-MM-DD')
                : ''
            }
          />
        );
        break;
      case 'multiple-fees':
        field = (
          <>
            {/* <InputLabel shrink id={name}>
                {label + (required ? ' *' : '')}
            </InputLabel> */}
            <FeesComponent value={feesValue}>
              {feeListValue.map((fee, index) => (
                <AccordionDetails className={classes.details} key={fee.feeType || `fee-${index}`}>
                  <TextField
                    fullWidth
                    labelid={fee.feeType}
                    label={fee.feeType}
                    value={fee.fee}
                    onBlur={(e) => {
                      if (e.target.value === '') {
                        updateFeeValue(fee.feeType, 0);
                      }
                    }}
                    onChange={(e) => {
                      updateFeeValue(fee.feeType, e.target.value);
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </AccordionDetails>
              ))}
            </FeesComponent>
          </>
        );
        break;
      case 'custom-select':
        field = (
          <div>
            <InputLabel shrink>{label}</InputLabel>
            <Select
              fullWidth
              value={getValue(name) || ''}
              onChange={(e) => setContraAccountDesignatorValue(e.target.value)}
            >
              <MenuItem value="">
                <em>Blank</em>
              </MenuItem>
              <MenuItem value="2">Long (2)</MenuItem>
              <MenuItem value="3">Short (3)</MenuItem>
            </Select>
          </div>
        );
        break;
      case 'autocomplete-contra-symbol':
        field = (
          <SelectCurrency
            freeSolo={false}
            name={name}
            label={label}
            required={required}
            value={getValue(name) || ''}
            onChange={(e) => {
              setContraSymbolValue(e.currentTarget.value);
              setGrossAmountValue('');
              setNetAmountValue('');
            }}
          />
        );
        break;
      default:
        field = <></>;
    }

    if (
      name === 'swap_rate' ||
      name === 'swap_rate_at' ||
      name === 'local_gross_amt' ||
      name === 'local_net_amt' ||
      name === 'swap_fee_bps_alpa' ||
      name === 'swap_fee_bps_corr' ||
      name === 'local_fees'
    ) {
      // Note: this will create a empty cell as place holder
      field = (contraSymbolValue || 'USD') === 'USD' ? <></> : field;
    }

    const autoWidth = rowCount > 2 ? '100%' : '300px';
    return (
      <div
        className={classes.grdCell1}
        key={rowKey}
        style={{ marginRight: 10, width: autoWidth }}
      >
        {' '}
        {field}{' '}
      </div>
    );
  }

  function renderForm() {
    const tradeElements = [
      [
        'entry_type',
        'correspondent',
        'account_number',
        'contra_correspondent',
        'contra_account',
        'side',
      ],
      [
        'symbol',
        'cusip',
        'isin',
        'exchangeMic',
        'qty',
        'price',
        'executed_at',
        'trade_date',
        'settle_date',
      ],
      [
        'gross_amount',
        (contraSymbolValue || 'USD') === 'USD' ? 'fees' : 'local_fees',
        'net_amount',
      ],
      ['description', 'external_id', 'execution_id', 'executing_venue'],
      ['order_id', 'leaves_qty', 'contra_account_designator', 'ref_id'],
      ['metadata', 'contra_symbol', 'swap_rate', 'swap_rate_at'],

      (contraSymbolValue || 'USD') === 'USD'
        ? null
        : ['swap_fee_bps_alpa', 'swap_fee_bps_corr'],
    ];

    const isSubtypeUSD = (subtype) => {
      if (
        subtype === 'REG' ||
        subtype === 'TAF' ||
        subtype === 'JIT' ||
        subtype === 'PROMO'
      ) {
        return true;
      }
      return (contraSymbolValue || 'USD') === 'USD';
    };
    const cashElements = [
      [
        'entry_type',
        'entry_sub_type',
        'correspondent',
        'account_number',
        'contra_correspondent',
      ],
      [
        'contra_account',
        isSubtypeUSD(entrySubTypeValue) ? 'net_amount' : 'local_net_amt',
        transactionValue === 'FXPNL' ? 'trade_date' : null,
        'settle_date',
        transactionValue === 'ACATC' ? 'hold_date' : null,
        'description',
      ],
      [
        'external_id',
        'execution_id',
        'vendor',
        transactionValue === 'INVPNL' ? 'symbol' : null,
        transactionValue === 'INVPNL' ? 'cusip' : null,
        ...(transactionValue === 'INVPNL' ? ['isin', 'exchangeMic'] : []),
        'ref_id',
      ],
      ['metadata', 'contra_symbol', 'swap_rate', 'swap_rate_at'],
    ];

    const positionElements = [
      [
        'entry_type',
        'entry_sub_type',
        'correspondent',
        'account_number',
        'contra_account',
        'contra_correspondent',
      ],
      [
        'symbol',
        'cusip',
        'isin',
        'exchangeMic',
        'qty',
        transactionValue !== 'MMSWP' ? 'price' : null,
        'settle_date',
        'hold_date',
      ],
      ['description', 'contra_account_designator', 'ref_id'],
      ['metadata', 'contra_symbol', 'swap_rate', 'swap_rate_at'],
    ];

    const cashPositionElements = [
      [
        'entry_type',
        'entry_sub_type',
        'correspondent',
        'account_number',
        'contra_correspondent',
      ],
      [
        'contra_account',
        'symbol',
        'cusip',
        'isin',
        'exchangeMic',
        'qty',
        'price',
      ],
      [
        transactionValue === 'FXCNV' ? 'local_net_amt' : 'net_amount',
        transactionValue === 'FXCNV' ? 'trade_date' : null,
        'settle_date',
        'hold_date',
        'description',
        'contra_account_designator',
      ],
      [
        'ref_id',
        'metadata',
        ...(transactionValue === 'FXCNV' ? [] : ['contra_symbol', 'swap_rate', 'swap_rate_at']),
      ],
    ];

    const journalCashPositionElements = [
      // unused?
      [
        'entry_type',
        'correspondent',
        'account_number',
        'contra_correspondent',
        'contra_account',
      ],
      [
        'symbol',
        'cusip',
        'qty',
        'net_amount',
        'price',
        'trade_date',
        'settle_date',
      ],
      ['description', 'external_id', 'execution_id', 'executing_venue'],
      ['order_id', 'leaves_qty', 'contra_account_designator', 'ref_id'],
      ['metadata'],
    ];

    let elements = [];
    if (screen === 1) elements = tradeElements;
    if (screen === 2) elements = cashElements;
    if (screen === 3) elements = positionElements;
    if (screen === 4) elements = cashPositionElements;
    if (screen === 5) elements = journalCashPositionElements; // unused?

    return getTransactionForm(elements, screen);
  }

  return <>{renderForm()}</>;
}
